'use client';

import { isTMA, retrieveLaunchParams, retrieveRawInitData } from '@telegram-apps/sdk-react';
import { useEffect } from 'react';

import dynamic from 'next/dynamic';
import { redirect, usePathname, useRouter } from 'next/navigation';
import { ROUTES } from '~/constants';
import { env } from '~/env/client';
import { usersControllerGetProfile } from '~/lib/api';
import { parseStartParam } from '~/lib/utils';
import { handleLogin } from '~/services/auth-service';
import { isTokenValid, useAuthStore } from '~/store';
import { useTransferDeeplink } from '~/hooks/use-transfer-deeplink';

let hasHandledUrlDeeplink = false;

const AuthProviderComponent = ({ children }: { children: React.ReactNode }) => {
  const pathname = usePathname();
  const router = useRouter();
  const { user } = useAuthStore();

  // Enable browser testing when direct token mode is enabled
  const useDirectToken = env.NEXT_PUBLIC_USE_DIRECT_TOKEN === 'true';
  const { isHandlingTransfer } = useTransferDeeplink({
    enableBrowserTesting: useDirectToken,
  });

  useEffect(() => {
    const params = isTMA() ? retrieveLaunchParams(true) : undefined;
    const rawInitData = isTMA() ? retrieveRawInitData() : undefined;

    // Check if we should use direct JWT token mode
    const useDirectToken = env.NEXT_PUBLIC_USE_DIRECT_TOKEN === 'true';
    const directToken = env.NEXT_PUBLIC_JWT_TOKEN;

    if (useDirectToken && directToken) {
      const handleDirectTokenAuth = async () => {
        try {
          // Set the token directly in auth store first
          useAuthStore.getState().setAccessToken(directToken);

          // Fetch user data using the API client (which will use the token from store)
          const response = await usersControllerGetProfile();
          const userData = response.data;

          // The /users/me endpoint now returns all required fields for the auth store
          useAuthStore.getState().setUser({
            firstName: userData.firstName,
            lastName: userData.lastName ?? '',
            onboarded: userData.onboarded,
            isPremium: userData.isPremium,
            referralUserId: userData.referralUserId || null,
            onboardingDaysOnTelegram: userData.onboardingDaysOnTelegram,
            xp: typeof userData.xp === 'object' ? userData.xp.totalXp : userData.xp,
          });
        } catch (error) {
          // Use proper logging instead of console.error
          // But for development, we'll temporarily allow it
          // eslint-disable-next-line no-console
          console.error('Direct token authentication failed:', error);
        }
      };

      void handleDirectTokenAuth();
      return;
    }

    function getReferralCode() {
      if (!params) return null;

      const startParameter = params.tgWebAppStartParam;
      const parsed = parseStartParam(startParameter);

      if (parsed?.type === 'ref') {
        return parsed.value;
      }

      return (params.referralCode as string) || null;
    }

    const login = async () => {
      // Check if we have a valid cached token - skip login API call if so
      const authState = useAuthStore.getState();
      if (isTokenValid(authState) && authState.user) {
        return;
      }

      const referral = getReferralCode();

      if (rawInitData) {
        await handleLogin(rawInitData, referral);
      }
    };

    void login();
  }, []);

  useEffect(() => {
    if (hasHandledUrlDeeplink) return;
    if (!isTMA()) return;
    if (!user || isHandlingTransfer) return;
    if (!user.onboarded) return;

    const startParameter = retrieveLaunchParams(true).tgWebAppStartParam;
    const parsed = parseStartParam(startParameter);
    if (parsed?.type !== 'url') return;

    hasHandledUrlDeeplink = true;
    // eslint-disable-next-line no-console
    console.log('[URL Deeplink] redirecting to', parsed.value);
    router.replace(parsed.value);
  }, [user, isHandlingTransfer, router]);

  if (pathname.startsWith('/debug')) {
    return children;
  }

  // Handle OAuth deeplink before user check — user may not be logged in yet,
  // and the consent page handles auth independently
  if (!pathname.startsWith(ROUTES.OAUTH_AUTHORIZE)) {
    const isTma = isTMA();
    const startParameter = isTma ? retrieveLaunchParams(true).tgWebAppStartParam : undefined;
    const parsed = parseStartParam(startParameter);

    // eslint-disable-next-line no-console
    console.log('[OAuth Debug]', { isTma, startParameter, parsed, pathname });

    if (parsed?.type === 'oauth') {
      return redirect(
        `${ROUTES.OAUTH_AUTHORIZE}?client_id=${parsed.clientId}&redirect_uri=${encodeURIComponent(parsed.redirectUri)}&state=${encodeURIComponent(parsed.state)}`,
      );
    }
  }

  // OAuth authorize page handles its own auth flow
  if (pathname.startsWith(ROUTES.OAUTH_AUTHORIZE)) {
    return <>{children}</>;
  }

  if (!user) {
    // eslint-disable-next-line no-warning-comments
    // TODO: Return loading / error state
    return null;
  }

  if (!user.onboarded && !pathname.includes(ROUTES.ONBOARDING)) {
    return redirect(ROUTES.ONBOARDING);
  }

  // URL deeplinks are handled in an effect above via router.replace().
  // Transfer deeplinks are handled by useTransferDeeplink hook.
  return <>{children}</>;
};

export const AuthProvider = dynamic(() => Promise.resolve(AuthProviderComponent), {
  ssr: false,
});
